import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  NgZone,
  AfterViewInit,
  WritableSignal,
  signal,
  SecurityContext,
  Signal,
  effect,
} from '@angular/core';
import { Params, Router, RouterLink } from '@angular/router';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState, ScreenSizes } from '@app/akita/screen/state/screen.store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { decode, BASE_16, getCanonicalProductUrl } from '@app/shared/utils/url.utils';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { Product } from '@app/akita/api/products/models/product.model';
import { WindowUtils } from '@app/shared/utils/window.util';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { MetaService } from '@app/shared/services/meta.service';
import { ProductVariant } from '@app/akita/api/products/models/product-variant.model';
import { FeaturedBuyButtonsComponent } from '../../../featured/components/featured-buy-buttons/featured-buy-buttons.component';
import { FooterComponent } from '../../../shared/components/new-footer/footer.component';
import { FeaturedListingCardComponent } from '../../../listing-details/components/featured-listing-card/featured-listing-card.component';
import { AsyncPipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AkitaRemoteConfigQuery } from '@app/akita/api/remote-config/state/remote-config.query';
import { VariantProductCardComponent } from '@app/variants/variant-product-card/variant-product-card.component';
import { PriceModel } from '@app/shared/models/api/price.model';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationBarComponent } from '../../../home-new/components/navigation-bar/navigation-bar.component';
import { LocalCartStorageService } from '@app/akita/cart/service/local-cart-storage.service';

export const MAX_PRICE_AMOUNT = 20000;

@Component({
  selector: 'app-product-details-view',
  templateUrl: './product-details.template.html',
  styleUrls: ['./product-details.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    CdkScrollable,
    RouterLink,
    FeaturedListingCardComponent,
    VariantProductCardComponent,
    FooterComponent,
    FeaturedBuyButtonsComponent,
    AsyncPipe,
    AmountPipe,
    NavigationBarComponent,
  ],
})
export class ProductDetailsViewComponent implements AfterViewInit, OnDestroy {
  public isBrowser: boolean;
  public readonly rtlMode$: Observable<boolean>;
  public readonly screenSize$: Observable<ScreenSizes>;
  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly homeUrlFragment$: Observable<Array<string>>;
  public readonly queryParamsToKeep$: Observable<Params>;

  public permanentLink: string;

  public readonly productId$: Observable<string | null>;
  public readonly product: Signal<Product | null>;
  public readonly productLoading$: Observable<boolean>;

  public testProductPage$: Observable<string>;
  public testProductPage: WritableSignal<string | null>;

  public totalPrice: WritableSignal<PriceModel | null>;

  public checkingVariant$: Observable<boolean>;
  private readonly checkingVariantSubject: BehaviorSubject<boolean>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly domSanitizer: DomSanitizer,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly translateService: TranslocoService,
    private readonly akitaRemoteConfigQuery: AkitaRemoteConfigQuery,
    private readonly localCartStorageService: LocalCartStorageService,
    private readonly meta: MetaService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.permanentLink = '';

    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.rtlMode$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.screenSize$ = this.akitaScreenQuery.selectSize();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.homeUrlFragment$ = this.akitaRouterQuery.selectHomeUrlFragment();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.testProductPage$ = this.akitaRemoteConfigQuery.selectTestProductPage();
    this.testProductPage = signal(null);

    this.totalPrice = signal(null);

    this.checkingVariantSubject = new BehaviorSubject(true);
    this.checkingVariant$ = this.checkingVariantSubject.asObservable();

    this.productId$ = this.akitaRouterQuery.selectParamsAndQueryParams().pipe(
      distinctUntilChanged((a, b) => a.tid === b.tid),
      map((params: Params): string | null => {
        // testing page
        if (params.t) {
          this.testProductPage.set(params.t);
        }

        return this.decodeProductId(`${params.tid || ''}`);
      })
    );

    this.product = toSignal(
      this.productId$.pipe(
        switchMap((productId: string | null) =>
          this.akitaProductsQuery.selectProduct(productId)
        ),
        distinctUntilChanged((a, b) => JSON.stringify(a?.id) === JSON.stringify(b?.id)),
        tap((product: Product | null) => {
          let description = `${
            product?.description || this.translateService.translate('app_title')
          }`;
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          if (description.length >= 150) {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            description = description.slice(0, 147) + '...';
          }
          const canonicalUrlSlug = this.domSanitizer
            .sanitize(SecurityContext.URL, getCanonicalProductUrl(product))
            ?.toString();

          // this.meta.checkRobotsNoIndexTag();
          this.meta.setTitle(`${product?.title || 'app_title'}`, true);
          this.meta.setCanonical(canonicalUrlSlug);
          this.meta.setAlternateHreflangsTags(product);
          this.meta.setTags([
            {
              name: 'og:url',
              content: `${canonicalUrlSlug || WindowUtils.getLocationHref() || ''}`,
            },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
            {
              name: 'og:image',
              content:
                product?.images?.[0] ||
                `https://www.popsy.app/assets/pwa/icon-512x512.png`,
            },
          ]);

          this.totalPrice.set(
            PriceModel.fromJson({
              amount:
                (product?.price?.amount || 0) +
                (product?.selectedInsurance?.price.amount || 0),
              currency: product?.price.currency || 'SAR',
            })
          );

          // adding to latest products picker
          if (product?.id) {
            this.localCartStorageService.saveProductID(product?.id);
          }
          if (!this.akitaRouterQuery.isGoogleBotUserAgent) {
            this.fetchVariantsInBG(product?.model);
            this.fetchOptionsInBG(product?.model);
          }
        })
      ),
      { initialValue: null }
    );

    effect(
      () => {
        this.subscriptions.add(
          this.akitaProductsQuery.selectProductVariants(this.product()?.model).subscribe({
            next: (variants: Array<ProductVariant>) => {
              if (variants.length) {
                const currentVariant = this.akitaProductsQuery.getProductVariant(
                  this.product()?.id,
                  this.product()?.model
                );

                // NOT IN USE FOR NOW (remove if not needed)
                // redirect if current variant is not in stock
                // prevent Google Bots from getting redirected
                try {
                  if (
                    !currentVariant?.inStock &&
                    this.akitaRouterQuery.isBrowser &&
                    (window as any)?.navigator &&
                    !(window as any)?.navigator.userAgent.toLowerCase().includes('google')
                  ) {
                    const cheapestAvailableVariant = variants
                      .sort((a, b) => (a.price?.amount || 0) - (b.price?.amount || 0))
                      .find((variant) => variant.inStock);
                    const sameColorStorage = variants.find(
                      (variant) =>
                        variant.storage === currentVariant?.storage &&
                        variant.color === currentVariant?.color &&
                        variant.inStock
                    );
                    const sameStorage = variants.find(
                      (variant) =>
                        variant.storage === currentVariant?.storage && variant.inStock
                    );

                    // navigate to cheapest available variant
                    if (cheapestAvailableVariant || sameColorStorage || sameStorage) {
                      this.router
                        .navigate(
                          this.akitaRouterQuery.getProductUrlFragment(
                            sameColorStorage?.id ||
                              sameStorage?.id ||
                              cheapestAvailableVariant?.id ||
                              '',
                            sameColorStorage?.title ||
                              sameStorage?.title ||
                              cheapestAvailableVariant?.title ||
                              ''
                          ),
                          { queryParamsHandling: 'merge' }
                        )
                        .catch((error: unknown) => {
                          console.warn(error);
                          this.checkingVariantSubject.next(false);
                        });
                    } else {
                      this.checkingVariantSubject.next(false);
                    }
                  } else {
                    this.checkingVariantSubject.next(false);
                  }
                  // this.checkingVariantSubject.next(false);
                } catch (error) {
                  console.warn(error);
                  this.checkingVariantSubject.next(false);
                }
              }
            },
            error: () => {},
          })
        );
      },
      {
        allowSignalWrites: true,
      }
    );

    this.productLoading$ = this.productId$.pipe(
      switchMap((productId: string | null) =>
        this.akitaProductsQuery.selectIsFetchingProduct(productId)
      )
    );

    this.subscriptions.add(
      combineLatest([this.productId$, this.akitaProductsQuery.selectValidateLocale()])
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: ([productId]) => {
            this.fetchProductInBG(productId);
          },
        })
    );
  }

  public ngAfterViewInit(): void {
    this.permanentLink = WindowUtils.getLocationHref();
  }

  private fetchProductInBG(productId?: string | null): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(this.akitaProductsService.getProductAsync(productId));
      });
    } else {
      this.subscriptions.add(this.akitaProductsService.getProductAsync(productId));
    }
  }

  private fetchVariantsInBG(model?: string | null): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService.getProductVariantsByModelAsync(model)
        );
      });
    }
  }

  private fetchOptionsInBG(model?: string | null): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService.getAvailableProductOptionsAsync(model || '')
        );
      });
    }
  }

  private decodeProductId(encoddedId?: string | null): string | null {
    let decoddedId: string | null = null;
    if (encoddedId) {
      const idSlices = `${encoddedId || ''}`.split('-');
      if (idSlices.length <= 1) {
        if (!idSlices[0]) {
          decoddedId = null;
        } else {
          decoddedId = idSlices[0];
        }
      } else {
        decoddedId = decode(idSlices[idSlices.length - 1], BASE_16);
      }
    }
    return decoddedId;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
